import { z } from 'zod';

export const QConfigGeneratorCreationFormSchema = z
  .object({
    overview: z
      .object({
        name: z.string({ message: 'กรุณากรอกชื่อ QConfig' }).nullable(),
        total: z.number({ message: 'กรุณาระบุจำนวน QConfig' }).nullable(),
      })
      .nullable(),

    questionBlueprint: z
      .object({
        total: z.number({ message: 'กรุณาระบุจำนวนข้อสอบ' }).nullable(),
        single: z.number({ message: 'กรุณาระบุจำนวนข้อ' }).nullable(),
        itemSet: z.number({ message: 'กรุณาระบุจำนวนข้อ' }).nullable(),
        calculated: z.number({ message: 'กรุณาระบุจำนวนข้อ' }).nullable(),
        nonCalculated: z.number({ message: 'กรุณาระบุจำนวนข้อ' }).nullable(),
        easy: z.number({ message: 'กรุณาระบุจำนวนข้อ' }).nullable(),
        medium: z.number({ message: 'กรุณาระบุจำนวนข้อ' }).nullable(),
        hard: z.number({ message: 'กรุณาระบุจำนวนข้อ' }).nullable(),
      })
      .nullable(),

    qbank: z
      .array(
        z.object(
          {
            qbankSelection: z
              .object({
                value: z.string().nullable(),
                label: z.string().nullable(),
              })
              .nullable(),
            levelItems: z.record(z.string(), z.boolean()).nullable(),
          },
          { message: 'กรุณาเลือกคลังข้อสอบ สำหรับสร้าง QConfig' },
        ),
        { message: 'กรุณาเลือกคลังข้อสอบ สำหรับสร้าง QConfig' },
      )
      .nullable(),
    chapterQuestionDistribution: z.record(z.number({ message: '' }).nonnegative()).nullable(),
  })
  .superRefine((data, ctx) => {
    const overview = data.overview;

    if (overview) {
      if (!overview.name || overview.name.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'กรุณากรอกชื่อ QConfig',
          path: ['overview', 'name'],
        });
      } else if (overview.name.length > 1024) {
        ctx.addIssue({
          code: 'custom',
          message: 'ความยาวต้องไม่เกิน 1024 ตัวอักษร',
          path: ['overview', 'name'],
        });
      }

      if (overview.total == null) {
        ctx.addIssue({
          code: 'custom',
          message: 'กรุณาระบุจำนวน QConfig',
          path: ['overview', 'total'],
        });
      } else {
        if (overview.total < 1) {
          ctx.addIssue({
            code: 'custom',
            message: 'จำนวน QConfig ต้องไม่น้อยกว่า 1 แบบ',
            path: ['overview', 'total'],
          });
        }
        if (overview.total > 10) {
          ctx.addIssue({
            code: 'custom',
            message: 'จำนวน QConfig ต้องไม่เกิน 10 แบบ',
            path: ['overview', 'total'],
          });
        }
      }
    }

    const qb = data.questionBlueprint;

    if (qb) {
      const {
        total = null,
        single = null,
        itemSet = null,
        calculated = null,
        nonCalculated = null,
        easy = null,
        medium = null,
        hard = null,
      } = qb;

      if (total == null) {
        ctx.addIssue({
          code: 'custom',
          message: 'กรุณาระบุจำนวนข้อสอบ',
          path: ['questionBlueprint', 'total'],
        });
      } else {
        if (total < 1) {
          ctx.addIssue({
            code: 'custom',
            message: 'จำนวนข้อสอบต้องไม่ต่ำกว่า 1 ข้อ',
            path: ['questionBlueprint', 'total'],
          });
        }
        if (total > 200) {
          ctx.addIssue({
            code: 'custom',
            message: 'จำนวนข้อสอบต้องไม่เกิน 200 ข้อ',
            path: ['questionBlueprint', 'total'],
          });
        }
      }

      const requiredFields: Array<[string, number | null]> = [
        ['single', single],
        ['itemSet', itemSet],
        ['calculated', calculated],
        ['nonCalculated', nonCalculated],
        ['easy', easy],
        ['medium', medium],
        ['hard', hard],
      ];

      for (const [field, value] of requiredFields) {
        if (value == null) {
          ctx.addIssue({
            code: 'custom',
            message: 'กรุณาระบุจำนวนข้อ',
            path: ['questionBlueprint', field],
          });
        }
      }

      if (typeof total === 'number') {
        if (typeof single === 'number' && typeof itemSet === 'number' && single + itemSet > total) {
          ctx.addIssue({
            code: 'custom',
            message: 'จำนวนข้อสอบเกินจากที่กำหนด',
            path: ['questionBlueprint', 'single'],
          });
          ctx.addIssue({
            code: 'custom',
            message: '',
            path: ['questionBlueprint', 'itemSet'],
          });
        }
        if (typeof single === 'number' && typeof itemSet === 'number' && single + itemSet < total) {
          ctx.addIssue({
            code: 'custom',
            message: 'จำนวนข้อสอบน้อยกว่าที่กำหนด',
            path: ['questionBlueprint', 'single'],
          });
          ctx.addIssue({
            code: 'custom',
            message: '',
            path: ['questionBlueprint', 'itemSet'],
          });
        }

        if (
          typeof easy === 'number' &&
          typeof medium === 'number' &&
          typeof hard === 'number' &&
          easy + medium + hard > total
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'จำนวนข้อสอบเกินจากที่กำหนด',
            path: ['questionBlueprint', 'easy'],
          });
          ctx.addIssue({
            code: 'custom',
            message: '',
            path: ['questionBlueprint', 'medium'],
          });
          ctx.addIssue({
            code: 'custom',
            message: '',
            path: ['questionBlueprint', 'hard'],
          });
        }
        if (
          typeof easy === 'number' &&
          typeof medium === 'number' &&
          typeof hard === 'number' &&
          easy + medium + hard < total
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'จำนวนข้อสอบน้อยกว่าที่กำหนด',
            path: ['questionBlueprint', 'easy'],
          });
          ctx.addIssue({
            code: 'custom',
            message: '',
            path: ['questionBlueprint', 'medium'],
          });
          ctx.addIssue({
            code: 'custom',
            message: '',
            path: ['questionBlueprint', 'hard'],
          });
        }

        if (
          typeof calculated === 'number' &&
          typeof nonCalculated === 'number' &&
          calculated + nonCalculated > total
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'จำนวนข้อสอบเกินจากที่กำหนด',
            path: ['questionBlueprint', 'calculated'],
          });
          ctx.addIssue({
            code: 'custom',
            message: '',
            path: ['questionBlueprint', 'nonCalculated'],
          });
        }
        if (
          typeof calculated === 'number' &&
          typeof nonCalculated === 'number' &&
          calculated + nonCalculated < total
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'จำนวนข้อสอบน้อยกว่าที่กำหนด',
            path: ['questionBlueprint', 'calculated'],
          });
          ctx.addIssue({
            code: 'custom',
            message: '',
            path: ['questionBlueprint', 'nonCalculated'],
          });
        }
      }
    }
  })
  .superRefine((data, ctx) => {
    const chapterQuestionDistribution = data.chapterQuestionDistribution;

    const overviewTotal = data.questionBlueprint?.total;

    if (!overviewTotal) return;

    const totalQuestions = Object.values(chapterQuestionDistribution ?? {}).reduce(
      (acc, curr) => acc + curr,
      0,
    );

    if (totalQuestions > overviewTotal) {
      ctx.addIssue({
        code: 'custom',
        message: 'จำนวนข้อสอบรายบทที่กำหนดเกินจำนวนข้อสอบทั้งหมด',
        path: ['chapterQuestionDistribution'],
      });
    }

    if (totalQuestions < overviewTotal) {
      ctx.addIssue({
        code: 'custom',
        message: 'จำนวนข้อสอบรายบทที่กำหนดน้อยกว่าจำนวนข้อสอบทั้งหมด',
        path: ['chapterQuestionDistribution'],
      });
    }
  });

export type QConfigGeneratorCreationFormInput = z.infer<typeof QConfigGeneratorCreationFormSchema>;
